import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { aurora, auroraPalette } from '@/components/aurora/tokens';

export interface MobileThreadHeaderMeta {
  title: string;
  subtitle?: string;
  avatarUrl?: string | null;
  presence?: 'online' | 'away' | 'offline';
}

interface MobileThreadViewProps {
  header: MobileThreadHeaderMeta;
  onBack: () => void;
  /** Slot azioni a destra dell'header (max 2, gestione a cura del chiamante). */
  headerActions?: ReactNode;
  /**
   * Area messaggi: scroll verticale interno. Il chiamante fornisce la lista
   * già renderizzata (bubble, separatori giorno, ecc.). Lo scroller resta
   * "owned" dal ThreadView per gestire il keyboard inset e l'auto-scroll.
   */
  children: ReactNode;
  /** Composer fissato in basso (testo + allegati + extra). */
  composer: ReactNode;
  /** Banner sticky sotto l'header (es. huddle attiva, errore connessione). */
  banner?: ReactNode;
  className?: string;
  /**
   * Se true, allo scroll vicino al fondo l'auto-scroll resta agganciato. Se
   * l'utente scrolla verso l'alto, viene rilasciato. Default: true.
   */
  stickToBottom?: boolean;
}

function initialsFor(title: string): string {
  const parts = title.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join('').padEnd(1, '·');
}

/**
 * Layout master-detail (vista "thread") per chat/canali/gruppi mobile:
 *
 * - Header sticky compatto (avatar + titolo + sottotitolo/presence + back)
 * - Area messaggi scrollabile flex-1, con safe-area top
 * - Composer fissato in basso, padding-bottom dinamico = altezza tastiera
 *   virtuale + env(safe-area-inset-bottom)
 *
 * Il keyboard inset usa `visualViewport.height` quando disponibile (iOS 13+
 * / Android Chrome). Su browser desktop o senza supporto si fallback su 0.
 */
export default function MobileThreadView({
  header,
  onBack,
  headerActions,
  children,
  composer,
  banner,
  className,
  stickToBottom = true,
}: MobileThreadViewProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [kbInset, setKbInset] = useState(0);
  const followBottomRef = useRef(true);

  // Virtual keyboard inset via visualViewport.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const layoutH = window.innerHeight;
      const visualH = vv.height;
      const delta = Math.max(0, layoutH - visualH - (vv.offsetTop || 0));
      setKbInset(delta);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // Stick-to-bottom: se l'utente è vicino al fondo manteniamo il pin quando
  // arrivano nuovi messaggi (children change).
  useEffect(() => {
    if (!stickToBottom) return;
    const el = scrollerRef.current;
    if (!el) return;
    if (followBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [children, stickToBottom, kbInset]);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    followBottomRef.current = distanceFromBottom < 80;
  };

  return (
    <div
      className={cn('flex flex-col h-full bg-background relative', className)}
      data-testid="mobile-thread-view"
    >
      {/* Header sticky */}
      <header
        className="sticky top-0 z-20 border-b border-border bg-background/85 backdrop-blur-md"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className="flex items-center gap-2 px-2 min-h-[56px]">
          <button
            type="button"
            onClick={onBack}
            aria-label="Indietro"
            className="h-11 w-11 inline-flex items-center justify-center rounded-full text-foreground/80 hover:bg-muted active:bg-muted/80 transition-colors focus:outline-none focus-visible:ring-2"
            style={{ outlineColor: auroraPalette.indigo }}
            data-testid="mobile-thread-back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="relative shrink-0">
            {header.avatarUrl ? (
              <img
                src={header.avatarUrl}
                alt=""
                className="h-9 w-9 rounded-full object-cover bg-muted"
                loading="lazy"
              />
            ) : (
              <div
                className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                style={{
                  background: `linear-gradient(135deg, ${auroraPalette.indigo}, ${auroraPalette.violet})`,
                }}
                aria-hidden
              >
                {initialsFor(header.title)}
              </div>
            )}
            {header.presence && header.presence !== 'offline' && (
              <span
                aria-hidden
                className={cn(
                  'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-background',
                  header.presence === 'online' ? 'bg-emerald-500' : 'bg-amber-400',
                )}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h1
              className={cn(aurora.type.title, 'truncate text-foreground')}
              data-testid="mobile-thread-title"
            >
              {header.title}
            </h1>
            {header.subtitle && (
              <p
                className={cn(aurora.type.caption, 'truncate text-muted-foreground')}
                data-testid="mobile-thread-subtitle"
              >
                {header.subtitle}
              </p>
            )}
          </div>
          {headerActions && (
            <div className="flex items-center gap-0.5" data-testid="mobile-thread-actions">
              {headerActions}
            </div>
          )}
        </div>
        {banner && <div data-testid="mobile-thread-banner">{banner}</div>}
      </header>

      {/* Messaggi */}
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overscroll-contain"
        data-testid="mobile-thread-scroller"
      >
        {children}
      </div>

      {/* Composer fisso in basso, con safe-area + keyboard inset */}
      <div
        className="sticky bottom-0 z-10 border-t border-border bg-background/95 backdrop-blur-md"
        style={{
          paddingBottom: `calc(env(safe-area-inset-bottom) + ${kbInset}px)`,
        }}
        data-testid="mobile-thread-composer"
      >
        {composer}
      </div>
    </div>
  );
}
